import React, { useState, useEffect, useMemo } from 'react';
import {
    Table,
    Box,
    SpaceBetween,
    Header,
    Spinner,
    Alert,
    ExpandableSection,
    ColumnLayout,
    Badge,
    Pagination,
    PropertyFilter,
    Container,
    Toggle,
} from '@amzn/awsui-components-react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import CashPositioningAPIClient from './cashPositioningAPIClient';
import { WireRecommendation, PositioningRegion, WireType } from './types';

interface WireRecommendationsTableProps {
    region: PositioningRegion;
    date: string;
    wireType: WireType;
    entityFilter?: string[];
    accountFilter?: string[];
    preloadedData?: WireRecommendation[];
    onDataLoaded?: (data: WireRecommendation[]) => void;
}

/** Entity group — aggregates recommendations for a single entity */
interface EntityGroup {
    entityId: string;
    currency: string;
    totalBalance: number;
    targetBalance: number;
    excessBalance: number;
    netPosition: number;
    classification: 'SURPLUS' | 'SHORTFALL' | 'NEUTRAL';
    accountCount: number;
    wireCount: number;
    noWireCount: number;
    recommendations: WireRecommendation[];
}

/**
 * Displays ZBA or AQ wire recommendations.
 * Two views:
 * 1. Entity-grouped view (default) — mirrors the prototype's CP results with
 *    expandable entity cards showing surplus/shortfall badges and net position
 * 2. Flat table view — full table with filtering, sorting, pagination
 */
const WireRecommendationsTable: React.FC<WireRecommendationsTableProps> = ({ region, date, wireType, entityFilter = [], accountFilter = [], preloadedData, onDataLoaded }) => {
    const [recommendations, setRecommendations] = useState<WireRecommendation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showFlatTable, setShowFlatTable] = useState(false);
    const [expandedEntities, setExpandedEntities] = useState<Record<string, boolean>>({});

    useEffect(() => {
        // Use preloaded data if available, otherwise fetch
        if (preloadedData && preloadedData.length > 0) {
            setRecommendations(preloadedData);
            setLoading(false);
            return;
        }

        let cancelled = false;
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = wireType === 'ZBA_SWEEP'
                    ? await CashPositioningAPIClient.getZbaPositioning(region, date)
                    : await CashPositioningAPIClient.getAqPositioning(region, date);
                if (!cancelled) {
                    const recs = response.recommendations || [];
                    setRecommendations(recs);
                    if (onDataLoaded) {
                        onDataLoaded(recs);
                    }
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Failed to load recommendations');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchData();
        return () => { cancelled = true; };
    }, [region, date, wireType, preloadedData]);

    // Group recommendations by entity
    const entityGroups: EntityGroup[] = useMemo(() => {
        // Apply parent-level filters first
        let filtered = recommendations;
        if (entityFilter.length > 0) {
            filtered = filtered.filter(r => entityFilter.includes(r.entityId || ''));
        }
        if (accountFilter.length > 0) {
            filtered = filtered.filter(r =>
                accountFilter.includes(r.sourceAccount || '') ||
                accountFilter.includes(r.destinationAccount || '') ||
                (r.balanceBreakdown && r.balanceBreakdown.some(b => accountFilter.includes(b.accountCode || '')))
            );
        }

        const groupMap: Record<string, WireRecommendation[]> = {};
        for (const rec of filtered) {
            const key = rec.entityId || 'Unknown';
            if (!groupMap[key]) groupMap[key] = [];
            groupMap[key].push(rec);
        }

        return Object.entries(groupMap).map(([entityId, recs]) => {
            const totalBalance = recs.reduce((sum, r) => sum + (r.totalBalance || 0), 0);
            const targetBalance = recs.reduce((sum, r) => sum + (r.targetBalance || 0), 0);
            const excessBalance = recs.reduce((sum, r) => sum + (r.excessBalance || 0), 0);
            const netPosition = excessBalance;
            const currency = recs[0]?.currency || '';
            const wireCount = recs.filter(r => r.wireType !== 'NO_WIRE').length;
            const noWireCount = recs.filter(r => r.wireType === 'NO_WIRE').length;

            let classification: 'SURPLUS' | 'SHORTFALL' | 'NEUTRAL';
            if (netPosition > 0) classification = 'SURPLUS';
            else if (netPosition < 0) classification = 'SHORTFALL';
            else classification = 'NEUTRAL';

            return {
                entityId,
                currency,
                totalBalance,
                targetBalance,
                excessBalance,
                netPosition,
                classification,
                accountCount: recs.length,
                wireCount,
                noWireCount,
                recommendations: recs,
            };
        }).sort((a, b) => Math.abs(b.netPosition) - Math.abs(a.netPosition));
    }, [recommendations, entityFilter, accountFilter]);

    // Summary counts
    const summaryCounts = useMemo(() => ({
        total: entityGroups.length,
        surplus: entityGroups.filter(g => g.classification === 'SURPLUS').length,
        shortfall: entityGroups.filter(g => g.classification === 'SHORTFALL').length,
        neutral: entityGroups.filter(g => g.classification === 'NEUTRAL').length,
        transfers: entityGroups.reduce((sum, g) => sum + g.wireCount, 0),
    }), [entityGroups]);

    // Filtered recommendations for flat table view (consistent with entity-grouped view)
    const filteredRecommendations = useMemo(() =>
        entityGroups.flatMap(g => g.recommendations),
    [entityGroups]);

    const formatAmount = (amount?: number, currency?: string) => {
        if (amount == null) return '—';
        const formatted = Math.abs(amount).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        const prefix = amount < 0 ? '-' : '';
        return `${prefix}${currency || ''} ${formatted}`.trim();
    };

    const getClassificationBadge = (classification: string) => {
        switch (classification) {
            case 'SURPLUS':
                return <Badge color="green">SURPLUS</Badge>;
            case 'SHORTFALL':
                return <Badge color="red">SHORTFALL</Badge>;
            case 'NEUTRAL':
            default:
                return <Badge color="grey">NEUTRAL</Badge>;
        }
    };

    const getWireTypeBadge = (type?: string) => {
        switch (type) {
            case 'ZBA_SWEEP':
                return <Badge color="blue">ZBA Sweep</Badge>;
            case 'AQ_POSITIONING':
                return <Badge color="green">AQ Transfer</Badge>;
            case 'NO_WIRE':
                return <Badge color="grey">No Wire</Badge>;
            default:
                return <Badge>{type || '—'}</Badge>;
        }
    };

    const toggleEntityExpand = (entityId: string) => {
        setExpandedEntities(prev => ({ ...prev, [entityId]: !prev[entityId] }));
    };

    if (loading) {
        return (
            <Box textAlign="center" padding="l">
                <Spinner size="large" />
                <Box variant="p" margin={{ top: 's' }}>
                    Loading {wireType === 'ZBA_SWEEP' ? 'ZBA' : 'AQ'} recommendations...
                </Box>
            </Box>
        );
    }

    if (error) {
        return <Alert type="error" header="Error">{error}</Alert>;
    }

    return (
        <SpaceBetween size="m">
            {/* Header with view toggle */}
            <Header
                counter={`(${recommendations.length})`}
                description={wireType === 'ZBA_SWEEP'
                    ? 'Zero Balance Account sweep recommendations — aggregates child balances and recommends sweeps to target accounts'
                    : 'Account Qualification positioning — identifies standalone accounts exceeding thresholds'}
                actions={
                    <Toggle
                        checked={showFlatTable}
                        onChange={({ detail }) => setShowFlatTable(detail.checked)}
                    >
                        Table view
                    </Toggle>
                }
            >
                {wireType === 'ZBA_SWEEP' ? 'ZBA Wire Recommendations' : 'AQ Wire Recommendations'}
            </Header>

            {showFlatTable ? (
                <FlatTableView
                    recommendations={filteredRecommendations}
                    formatAmount={formatAmount}
                    getWireTypeBadge={getWireTypeBadge}
                />
            ) : (
                /* Entity-grouped view */
                <SpaceBetween size="m">
                    {/* Summary bar */}
                    <Container>
                        <SpaceBetween direction="horizontal" size="l">
                            <Box>
                                <Box variant="awsui-key-label">CP Results for {date}</Box>
                                <Badge color="blue">{summaryCounts.total} entities</Badge>
                            </Box>
                            <Box>
                                <span style={{ color: '#037f0c', fontWeight: 600 }}>● {summaryCounts.surplus} Surplus</span>
                            </Box>
                            <Box>
                                <span style={{ color: '#d91515', fontWeight: 600 }}>● {summaryCounts.shortfall} Shortfall</span>
                            </Box>
                            <Box>
                                <span style={{ color: '#5f6b7a', fontWeight: 600 }}>● {summaryCounts.neutral} Neutral</span>
                            </Box>
                            <Box>
                                <span style={{ color: '#0972d3', fontWeight: 600 }}>↗ {summaryCounts.transfers} Transfers</span>
                            </Box>
                        </SpaceBetween>
                    </Container>

                    {/* Entity cards */}
                    {entityGroups.length === 0 ? (
                        <Box textAlign="center" padding="l">
                            <Box variant="p">No entities match the current filters.</Box>
                        </Box>
                    ) : (
                        entityGroups.map(group => (
                            <Container key={group.entityId}>
                                <ExpandableSection
                                    expanded={expandedEntities[group.entityId] || false}
                                    onChange={() => toggleEntityExpand(group.entityId)}
                                    headerText=""
                                    header={
                                        <SpaceBetween direction="horizontal" size="m">
                                            <Box fontWeight="bold" fontSize="body-m">
                                                {group.entityId}
                                            </Box>
                                            <Box color="text-body-secondary" fontSize="body-s">
                                                {group.currency} · {group.accountCount} account{group.accountCount !== 1 ? 's' : ''}
                                            </Box>
                                            <Box>
                                                {getClassificationBadge(group.classification)}
                                            </Box>
                                            <Box textAlign="right" fontWeight="bold">
                                                {formatAmount(group.netPosition, group.currency)}
                                            </Box>
                                        </SpaceBetween>
                                    }
                                >
                                    <SpaceBetween size="m">
                                        {/* Account-level cards from balanceBreakdown */}
                                        {group.recommendations.map((rec, recIdx) => (
                                            rec.balanceBreakdown && rec.balanceBreakdown.length > 0
                                                ? rec.balanceBreakdown.map((acct, idx) => {
                                                    const netPos = (acct.positioningResult || 0) - (acct.targetBalance || 0);
                                                    const acctClassification = netPos > 0 ? 'SURPLUS' : netPos < 0 ? 'SHORTFALL' : 'NEUTRAL';
                                                    const actionLabel = (acct.wireRecommendationAmount || 0) !== 0
                                                        ? `TRANSFER ${formatAmount(Math.abs(acct.wireRecommendationAmount), acct.currency)}`
                                                        : 'NO ACTION';
                                                    return (
                                                        <Container key={`${rec.entityId}-${recIdx}-${acct.accountCode}-${idx}`}>
                                                            {/* Account header */}
                                                            <SpaceBetween size="s">
                                                                <SpaceBetween direction="horizontal" size="m">
                                                                    <Box fontWeight="bold">{acct.accountCode}</Box>
                                                                    <Box color="text-body-secondary" fontSize="body-s">
                                                                        {rec.entityId} · {acct.currency}
                                                                    </Box>
                                                                    <Box>{getClassificationBadge(acctClassification)}</Box>
                                                                    <Box textAlign="right" fontWeight="bold">
                                                                        {formatAmount(netPos, acct.currency)}
                                                                    </Box>
                                                                </SpaceBetween>

                                                                {/* Balance cards row */}
                                                                <ColumnLayout columns={4} variant="text-grid">
                                                                    <div>
                                                                        <Box variant="awsui-key-label">OPENING BALANCE</Box>
                                                                        <Box fontWeight="bold">
                                                                            {formatAmount(acct.openingBalance, acct.currency)}
                                                                        </Box>
                                                                    </div>
                                                                    <div>
                                                                        <Box variant="awsui-key-label">INTRADAY ADJ</Box>
                                                                        <Box fontWeight="bold">
                                                                            {formatAmount(acct.intradayNet, acct.currency)}
                                                                        </Box>
                                                                    </div>
                                                                    <div>
                                                                        <Box variant="awsui-key-label">POSITIONING BALANCE</Box>
                                                                        <Box fontWeight="bold">
                                                                            {formatAmount(acct.positioningResult, acct.currency)}
                                                                        </Box>
                                                                    </div>
                                                                    <div>
                                                                        <Box variant="awsui-key-label">TARGET BALANCE</Box>
                                                                        <Box fontWeight="bold">
                                                                            {formatAmount(acct.targetBalance, acct.currency)}
                                                                        </Box>
                                                                    </div>
                                                                </ColumnLayout>

                                                                {/* Action row */}
                                                                <SpaceBetween direction="horizontal" size="m">
                                                                    <Box fontWeight="bold" fontSize="body-s">
                                                                        {actionLabel}
                                                                    </Box>
                                                                    {(acct.wireRecommendationAmount || 0) === 0 && (
                                                                        <Box color="text-body-secondary" fontSize="body-s">
                                                                            Balance at target
                                                                        </Box>
                                                                    )}
                                                                </SpaceBetween>
                                                            </SpaceBetween>
                                                        </Container>
                                                    );
                                                })
                                                : (
                                                    /* Fallback: show entity-level summary if no balanceBreakdown */
                                                    <Container key={`${rec.entityId}-${recIdx}-fallback`}>
                                                        <ColumnLayout columns={4} variant="text-grid">
                                                            <div>
                                                                <Box variant="awsui-key-label">Total Balance</Box>
                                                                <Box>{formatAmount(rec.totalBalance, rec.currency)}</Box>
                                                            </div>
                                                            <div>
                                                                <Box variant="awsui-key-label">Target Balance</Box>
                                                                <Box>{formatAmount(rec.targetBalance, rec.currency)}</Box>
                                                            </div>
                                                            <div>
                                                                <Box variant="awsui-key-label">Excess Balance</Box>
                                                                <Box fontWeight="bold">
                                                                    {formatAmount(rec.excessBalance, rec.currency)}
                                                                </Box>
                                                            </div>
                                                            <div>
                                                                <Box variant="awsui-key-label">Wire Type</Box>
                                                                <Box>{getWireTypeBadge(rec.wireType)}</Box>
                                                            </div>
                                                        </ColumnLayout>
                                                    </Container>
                                                )
                                        ))}

                                        {/* Reasoning */}
                                        {group.recommendations[0]?.reasoning && (
                                            <Box>
                                                <Box variant="awsui-key-label">Reasoning</Box>
                                                <Box color="text-body-secondary">
                                                    {group.recommendations[0].reasoning}
                                                </Box>
                                            </Box>
                                        )}
                                    </SpaceBetween>
                                </ExpandableSection>
                            </Container>
                        ))
                    )}
                </SpaceBetween>
            )}
        </SpaceBetween>
    );
};

/** Flat table view — the original full table with all columns */
const FlatTableView: React.FC<{
    recommendations: WireRecommendation[];
    formatAmount: (amount?: number, currency?: string) => string;
    getWireTypeBadge: (type?: string) => JSX.Element;
}> = ({ recommendations, formatAmount, getWireTypeBadge }) => {
    const columnDefinitions = [
        {
            id: 'entityId',
            header: 'Entity',
            cell: (item: WireRecommendation) => item.entityId || '—',
            sortingField: 'entityId',
            width: 140,
        },
        {
            id: 'sourceAccount',
            header: 'Source Account',
            cell: (item: WireRecommendation) => item.sourceAccount || '—',
            sortingField: 'sourceAccount',
            width: 160,
        },
        {
            id: 'destinationAccount',
            header: 'Destination Account',
            cell: (item: WireRecommendation) => item.destinationAccount || '—',
            sortingField: 'destinationAccount',
            width: 160,
        },
        {
            id: 'amount',
            header: 'Amount',
            cell: (item: WireRecommendation) => (
                <Box textAlign="right" fontWeight="bold">
                    {formatAmount(item.amount, item.currency)}
                </Box>
            ),
            sortingField: 'amount',
            width: 160,
        },
        {
            id: 'wireType',
            header: 'Type',
            cell: (item: WireRecommendation) => getWireTypeBadge(item.wireType),
            sortingField: 'wireType',
            width: 120,
        },
        {
            id: 'totalBalance',
            header: 'Total Balance',
            cell: (item: WireRecommendation) => formatAmount(item.totalBalance, item.currency),
            sortingField: 'totalBalance',
            width: 150,
        },
        {
            id: 'targetBalance',
            header: 'Target Balance',
            cell: (item: WireRecommendation) => formatAmount(item.targetBalance, item.currency),
            sortingField: 'targetBalance',
            width: 150,
        },
        {
            id: 'excessBalance',
            header: 'Excess Balance',
            cell: (item: WireRecommendation) => formatAmount(item.excessBalance, item.currency),
            sortingField: 'excessBalance',
            width: 150,
        },
        {
            id: 'approvalStatus',
            header: 'Approval',
            cell: (item: WireRecommendation) => {
                switch (item.approvalStatus) {
                    case 'APPROVED': return <Badge color="green">Approved</Badge>;
                    case 'REJECTED': return <Badge color="red">Rejected</Badge>;
                    default: return <Badge color="blue">Pending</Badge>;
                }
            },
            sortingField: 'approvalStatus',
            width: 110,
        },
        {
            id: 'wireDate',
            header: 'Wire Date',
            cell: (item: WireRecommendation) => item.wireDate || '—',
            sortingField: 'wireDate',
            width: 110,
        },
    ];

    const filteringProperties = [
        { key: 'entityId', propertyLabel: 'Entity', groupValuesLabel: 'Entity values', operators: [':', '='] as const },
        { key: 'sourceAccount', propertyLabel: 'Source Account', groupValuesLabel: 'Source values', operators: [':', '='] as const },
        { key: 'destinationAccount', propertyLabel: 'Destination Account', groupValuesLabel: 'Dest values', operators: [':', '='] as const },
        { key: 'currency', propertyLabel: 'Currency', groupValuesLabel: 'Currency values', operators: [':', '='] as const },
        { key: 'approvalStatus', propertyLabel: 'Approval Status', groupValuesLabel: 'Status values', operators: [':', '='] as const },
    ];

    const { items, collectionProps, propertyFilterProps, paginationProps } = useCollection(
        recommendations,
        {
            propertyFiltering: { filteringProperties },
            sorting: { defaultState: { sortingColumn: { sortingField: 'amount' }, isDescending: true } },
            pagination: { pageSize: 20 },
            selection: {},
        }
    );

    return (
        <Table
            {...collectionProps}
            items={items}
            columnDefinitions={columnDefinitions}
            stickyHeader
            resizableColumns
            trackBy="processingDateWireType"
            filter={
                <PropertyFilter
                    {...propertyFilterProps}
                    i18nStrings={{
                        filteringPlaceholder: 'Filter recommendations...',
                        groupValuesText: 'Values',
                        groupPropertiesText: 'Properties',
                        operatorsText: 'Operators',
                        operationAndText: 'and',
                        operationOrText: 'or',
                        operatorLessText: 'Less than',
                        operatorLessOrEqualText: 'Less than or equal',
                        operatorGreaterText: 'Greater than',
                        operatorGreaterOrEqualText: 'Greater than or equal',
                        operatorContainsText: 'Contains',
                        operatorDoesNotContainText: 'Does not contain',
                        operatorEqualsText: 'Equals',
                        operatorDoesNotEqualText: 'Does not equal',
                        editTokenHeader: 'Edit filter',
                        propertyText: 'Property',
                        operatorText: 'Operator',
                        valueText: 'Value',
                        cancelActionText: 'Cancel',
                        applyActionText: 'Apply',
                        clearFiltersText: 'Clear filters',
                        removeTokenButtonAriaLabel: () => 'Remove token',
                        enteredTextLabel: (text: string) => `Use: "${text}"`,
                    }}
                />
            }
            pagination={<Pagination {...paginationProps} />}
            empty={
                <Box textAlign="center" padding="l">
                    <Box variant="p">No recommendations for this region and date.</Box>
                </Box>
            }
        />
    );
};

export default WireRecommendationsTable;
